import { API_BASE_URL } from './config.js';
import { authStorage } from '../tenancy/authStorage.js';
import { getTenantSlug } from '../tenancy/tenant.js';
import { getActiveLanguage, languageStore } from '../i18n/languageStore.js';

const nativeFetch = typeof window !== 'undefined' && window.fetch
  ? window.fetch.bind(window)
  : typeof fetch === 'function'
    ? fetch.bind(globalThis)
    : null;

export const MANAGED_DETAIL_REQUEST = Symbol('atrux.managed-detail-request');

const PUBLIC_PATH_PATTERNS = [
  /^\/temporary-access-info\/?$/,
  /^\/temporary-access-cmr\//,
  /^\/temporary-access-sign\/?$/,
  /^\/ecmr\/verify\//,
  /^\/ecmr\/driver-login\/?$/,
];

const NO_AUTH_PATH_PATTERNS = [
  /^\/languages\/?$/,
  /^\/auth\/login\/?$/,
  /^\/auth\/2fa\/(verify|resend)\/?$/,
  /^\/auth\/users\/reset_password\/?$/,
  /^\/auth\/users\/reset_password_confirm\/?$/,
];

// The language registry is required to establish the first active locale and
// its payload is locale-independent. Never delay bootstrap by replaying it.
const LOCALE_REPLAY_EXEMPT_PATH_PATTERNS = [
  /^\/languages\/?$/,
];

let localeEpoch = 0;
let observedLanguage = getActiveLanguage();
const inFlightLocaleAttempts = new Set();

languageStore.subscribe((snapshot) => {
  const nextLanguage = snapshot?.activeLanguage;
  if (!nextLanguage || nextLanguage === observedLanguage) return;

  observedLanguage = nextLanguage;
  localeEpoch += 1;
  inFlightLocaleAttempts.forEach((attempt) => attempt.invalidate());
});

const toUrl = (input) => {
  if (typeof input === 'string' || input instanceof URL) {
    return new URL(String(input), typeof window !== 'undefined' ? window.location.origin : API_BASE_URL);
  }
  return new URL(input.url);
};

const isAtruxApiRequest = (input) => {
  try {
    const requestUrl = toUrl(input);
    const apiUrl = new URL(API_BASE_URL);
    const apiPath = apiUrl.pathname.replace(/\/+$/, '');
    return requestUrl.origin === apiUrl.origin
      && (!apiPath || requestUrl.pathname === apiPath || requestUrl.pathname.startsWith(`${apiPath}/`));
  } catch {
    return false;
  }
};

const apiRelativePath = (input) => {
  const requestUrl = toUrl(input);
  const apiUrl = new URL(API_BASE_URL);
  const apiPath = apiUrl.pathname.replace(/\/+$/, '');
  return requestUrl.pathname.slice(apiPath.length) || '/';
};

const hasHeader = (headers, name) => new Headers(headers || {}).has(name);

const isPublicApiRequest = (input, headers) => {
  if (hasHeader(headers, 'X-Temp-Code')) return true;
  try {
    const path = apiRelativePath(input);
    return PUBLIC_PATH_PATTERNS.some((pattern) => pattern.test(path));
  } catch {
    return false;
  }
};

const requestMethod = (input, init) => String(
  init.method
    || (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET')
    || 'GET'
).toUpperCase();

const shouldReplayForLocale = (input, init, options) => {
  if (options.localeReplay === false || requestMethod(input, init) !== 'GET') return false;

  try {
    const path = apiRelativePath(input);
    return !LOCALE_REPLAY_EXEMPT_PATH_PATTERNS.some((pattern) => pattern.test(path));
  } catch {
    return false;
  }
};

const getSourceSignal = (input, init) => (
  init.signal
    || (typeof Request !== 'undefined' && input instanceof Request ? input.signal : undefined)
);

const createLocaleAttempt = (sourceSignal) => {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let invalidated = false;
  let removeSourceAbortListener = () => {};

  if (controller && sourceSignal) {
    const abortFromSource = () => controller.abort(sourceSignal.reason);
    if (sourceSignal.aborted) {
      abortFromSource();
    } else {
      sourceSignal.addEventListener('abort', abortFromSource, { once: true });
      removeSourceAbortListener = () => sourceSignal.removeEventListener('abort', abortFromSource);
    }
  }

  return {
    signal: controller?.signal || sourceSignal,
    get invalidated() {
      return invalidated;
    },
    invalidate() {
      invalidated = true;
      if (controller && !controller.signal.aborted) controller.abort();
    },
    cleanup() {
      removeSourceAbortListener();
    },
  };
};

const localeChangedSince = (language, epoch) => (
  localeEpoch !== epoch || getActiveLanguage() !== language
);

const isJsonResponse = (response) => {
  const contentType = response?.headers?.get?.('Content-Type') || '';
  return /(?:^|[+/])json(?:\s*;|$)/i.test(contentType);
};

const bufferLocaleSensitiveResponse = async (response, localeReplay) => {
  // A fetch promise resolves as soon as the response headers are available.
  // Buffer JSON while the locale attempt is still registered so a language
  // switch that happens during response.json() cannot leak the old locale.
  if (!localeReplay || !isJsonResponse(response)) return response;

  // Reading a clone drains the network stream while leaving the original
  // Response intact. Returning that original preserves fetch metadata such as
  // url, redirected and type, while its tee'd branch is fully buffered for the
  // consumer's later response.json() call.
  await response.clone().arrayBuffer();
  return response;
};

const redirectForTenantError = async (
  response,
  authenticatedRequest = false,
  isSessionCurrent = () => true,
  deferTemporaryTenantUnavailable = false,
) => {
  if ([404, 503].includes(response.status)) {
    const unavailablePayload = await response.clone().json().catch(() => null);
    if (unavailablePayload?.error === 'tenant_unavailable' && typeof window !== 'undefined') {
      if (response.status === 503 && deferTemporaryTenantUnavailable) return false;
      window.dispatchEvent(new CustomEvent('atrux:tenant-unavailable', {
        detail: { status: response.status },
      }));
      return false;
    }
  }

  if (response.status === 403) {
    const forbiddenPayload = await response.clone().json().catch(() => null);
    if (typeof forbiddenPayload?.detail === 'string' && forbiddenPayload.detail.includes('Feature-ul')) {
      window.dispatchEvent(new CustomEvent('atrux:features-stale'));
    }
    return false;
  }

  if (![400, 401].includes(response.status)) return false;

  const payload = await response.clone().json().catch(() => null);
  const tenantError = payload && ['tenant_conflict', 'invalid_tenant_token'].includes(payload.error);
  if (!tenantError && !(response.status === 401 && authenticatedRequest)) return false;

  // The response belongs to the credentials used for this attempt. Never let
  // a late 401 from a previous login clear a newer browser session.
  if (!isSessionCurrent()) return false;

  authStorage.clearSession();
  if (typeof window === 'undefined') return true;

  window.dispatchEvent(new CustomEvent('atrux:auth-revoked', {
    detail: {
      status: response.status,
      reason: payload?.error || 'unauthorized',
      message: response.status === 401
        ? 'Sesiunea a expirat sau a fost revocată.'
        : 'Sesiunea nu este validă pentru firma curentă.',
    },
  }));

  const reason = payload?.error === 'tenant_conflict' ? '?reason=tenant-conflict' : '?reason=session-revoked';
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign(`/login${reason}`);
  }
  return true;
};

const createSessionChangedError = () => {
  const error = new Error('The authenticated session changed while the request was in flight.');
  error.name = 'AbortError';
  error.code = 'session_changed';
  return error;
};

export async function apiFetch(input, init = {}, options = {}) {
  if (!nativeFetch) throw new Error('Fetch API is not available');

  const sourceHeaders = init.headers || (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined);
  const initialHeaders = new Headers(sourceHeaders || {});
  const publicRequest = options.publicRequest === true || isPublicApiRequest(input, initialHeaders);
  const path = publicRequest ? null : apiRelativePath(input);
  const noAuth = publicRequest
    || options.auth === false
    || NO_AUTH_PATH_PATTERNS.some((pattern) => pattern.test(path));
  const sessionIdentity = publicRequest ? null : {
    tenantSlug: getTenantSlug(),
    token: noAuth ? null : authStorage.getToken(),
  };
  const isSessionCurrent = () => (
    publicRequest
    || (
      getTenantSlug() === sessionIdentity.tenantSlug
      && (noAuth || authStorage.getToken() === sessionIdentity.token)
    )
  );

  const createAttemptHeaders = () => {
    const headers = new Headers(sourceHeaders || {});
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');

    if (publicRequest) {
      headers.delete('Authorization');
      headers.delete('X-Tenant-Slug');
      return headers;
    }

    headers.set('X-Tenant-Slug', sessionIdentity.tenantSlug);
    if (sessionIdentity.token && !noAuth && !headers.has('Authorization')) {
      headers.set('Authorization', `Token ${sessionIdentity.token}`);
    }
    return headers;
  };

  const localeReplay = shouldReplayForLocale(input, init, options);
  const sourceSignal = getSourceSignal(input, init);

  // Keep the original promise alive across a language change. Consumers see
  // only the response matching the current locale, so they do not need to
  // handle an AbortError or coordinate a separate refetch.
  while (true) {
    if (!isSessionCurrent()) throw createSessionChangedError();
    const attemptLanguage = getActiveLanguage();
    const attemptEpoch = localeEpoch;
    const attemptHeaders = createAttemptHeaders();
    if (attemptLanguage) attemptHeaders.set('Accept-Language', attemptLanguage);

    const attempt = localeReplay ? createLocaleAttempt(sourceSignal) : null;
    if (attempt) inFlightLocaleAttempts.add(attempt);

    let response;
    try {
      response = await nativeFetch(input, {
        ...init,
        headers: attemptHeaders,
        ...(attempt?.signal ? { signal: attempt.signal } : {}),
      });
      response = await bufferLocaleSensitiveResponse(response, localeReplay);
    } catch (error) {
      if (!isSessionCurrent()) throw createSessionChangedError();
      const replayAfterLocaleChange = localeReplay
        && !sourceSignal?.aborted
        && (attempt?.invalidated || localeChangedSince(attemptLanguage, attemptEpoch));
      if (replayAfterLocaleChange) continue;
      throw error;
    } finally {
      if (attempt) {
        inFlightLocaleAttempts.delete(attempt);
        attempt.cleanup();
      }
    }

    if (!isSessionCurrent()) throw createSessionChangedError();
    if (localeReplay && localeChangedSince(attemptLanguage, attemptEpoch)) continue;

    let clearedCurrentSession = false;
    if (!publicRequest) {
      clearedCurrentSession = await redirectForTenantError(
        response,
        attemptHeaders.has('Authorization'),
        isSessionCurrent,
        options.deferTemporaryTenantUnavailable === true,
      );
    }

    // redirectForTenantError may parse a cloned response asynchronously. Guard
    // that interval as well before exposing the response to application code.
    if (!clearedCurrentSession && !isSessionCurrent()) throw createSessionChangedError();
    if (localeReplay && localeChangedSince(attemptLanguage, attemptEpoch)) continue;
    return response;
  }
}

let interceptorInstalled = false;

export function installTenantFetchInterceptor() {
  if (interceptorInstalled || typeof window === 'undefined' || !nativeFetch) return;

  window.fetch = (input, init = {}) => {
    const managedDetailRequest = init?.[MANAGED_DETAIL_REQUEST] === true;
    const cleanInit = { ...init };
    delete cleanInit[MANAGED_DETAIL_REQUEST];
    return isAtruxApiRequest(input)
      ? apiFetch(input, cleanInit, {
        deferTemporaryTenantUnavailable: managedDetailRequest,
      })
      : nativeFetch(input, cleanInit);
  };
  interceptorInstalled = true;
}

export function tenantHeaders(headers = {}) {
  const nextHeaders = new Headers(headers);
  nextHeaders.set('X-Tenant-Slug', getTenantSlug());
  return Object.fromEntries(nextHeaders.entries());
}
